import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { QRCODE_ATTEMPT_ACTION } from '~redux/reducers/attemptQrcode';
import { ERROR_ACTIONS } from '~redux/reducers/error';
import { INTENT_ACTION } from '~redux/reducers/intent';
import { v4 as uuid } from 'uuid';
import { UAParser } from 'ua-parser-js';
import { buildHeaders } from './confirm';
import { getRequest, getIntent, getQrcode } from '../selectors';
import { POST_EVENTS, sendEvent, ExtendElementType } from '~components/events';
import { PaymentMethodWithQrcode } from '@airwallex/airwallex-payment-elements';
import { getLang, getCheckoutApiUrl, getAirwayUrl, sendRequestLog, isHPP, isMobileDevice } from '~components/utils';

interface CreatePaymentAttemptPayload {
  intent_id: string;
  method: PaymentMethodWithQrcode;
  componentName: ExtendElementType;
}

const QRCODE_METHOD_MAP = {
  qrcode: 'webqr',
  jsapi: 'jsapi',
  mweb: 'mweb',
};

export const buildQrcodeAttemptPayload = (method: PaymentMethodWithQrcode, successUrl: string) => {
  // if it's wechat pay
  if (method === 'wechatpay') {
    const parser = new UAParser();
    const { browser } = parser.getResult() || {};
    // if it's wechat browser
    if (
      // @ts-ignore
      typeof WeixinJSBridge !== 'undefined' || // The Lazy guy wechat init, most time it not ready
      browser?.name === 'WeChat'
    ) {
      return {
        payment_method: {
          type: method,
          [method]: {
            flow: QRCODE_METHOD_MAP.jsapi,
          },
        },
        return_url: successUrl,
      };
    }

    // if it's android or ios, but not wechat browser
    if (isMobileDevice()) {
      return {
        payment_method: {
          type: method,
          [method]: {
            flow: QRCODE_METHOD_MAP.mweb,
          },
        },
        return_url: isHPP()
          ? `${window.location.origin}/#/page/redirect-wechat?redirectUrl=${encodeURIComponent(successUrl)}`
          : successUrl,
      };
    }
  }

  return {
    payment_method: {
      type: method,
      [method]: {
        flow: QRCODE_METHOD_MAP.qrcode,
      },
    },
    return_url: successUrl,
  };
};

export function* createPaymentAttemptAsync(data: { type: string; payload: CreatePaymentAttemptPayload }) {
  try {
    const { intent_id, method, componentName } = data?.payload;
    let dataUrl = yield select(getQrcode);
    const request = yield select(getRequest);
    const reqPath = `${getAirwayUrl()}/payment_intents/${intent_id}/confirm`;
    const reqHeader = buildHeaders(request);
    const reqPayload = {
      ...buildQrcodeAttemptPayload(method, request.successUrl),
      request_id: uuid(),
    };
    sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const {
      data: { next_action: nextAction },
    } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
    sendEvent(
      {
        type: componentName,
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: data,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    if (nextAction?.qrcode_url) {
      const QRCode = yield import('qrcode');
      const qrUrl: string = nextAction?.qrcode_url || '';
      let originUrl = qrUrl;
      // If wechat non prod, go to mock page, others just render the qrcode
      if (qrUrl.startsWith('http') && method === 'wechatpay') {
        originUrl = `${window.location.origin}/#/qrcode/mock?hook=${encodeURIComponent(qrUrl)}&lang=${getLang()}`;
      }
      dataUrl = yield QRCode.toDataURL(originUrl);
      yield put({ type: QRCODE_ATTEMPT_ACTION, payload: { dataUrl, originUrl } });

      let intent = yield select(getIntent);
      let qrcodeAttempt = dataUrl;
      while (intent?.status !== 'SUCCEEDED' && qrcodeAttempt !== '') {
        const { data: result } = yield call([axios, 'get'], `${getCheckoutApiUrl()}/wechat/callback/${intent_id}`, {
          ...buildHeaders(request),
        });
        intent = result;
        qrcodeAttempt = yield select(getQrcode);
      }
      yield put({ type: INTENT_ACTION, payload: intent });
    } else if (nextAction?.type === 'redirect') {
      const redirectUrl = nextAction?.url + '&timestamp=' + (new Date().getTime() + uuid());
      if (request?.origin) {
        sendEvent(
          {
            type: componentName,
            code: POST_EVENTS.onRedirect,
            redirectUrl,
          },
          request?.origin,
        );
        return;
      } else {
        window.location.href = redirectUrl;
        return;
      }
    }
  } catch (error) {
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
  }
}

export function* watchCreateAttemptQrcodeAsync() {
  yield takeLatest('AWX_CHECKOUT_ATTEMPT_QRCODE_ASYNC', createPaymentAttemptAsync);
}
